import React, {
  useState,
  forwardRef,
  useImperativeHandle
} from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Tooltip
} from "@material-ui/core";

import ttAPI from '../../api/ttAPI';
import useStyles from "./styles";
import config from '../../config';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';

const NearbyJobsDialog = forwardRef((props, ref) => {
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [referenceAddress, setReferenceAddress] = useState("");
  const [referenceRetest, setReferenceRetest] = useState(null);

  const [sortBy, setSortBy] = useState('distance');
  const [sortDirection, setSortDirection] = useState('asc');

  useImperativeHandle(ref, () => ({
    openNearbyJobsPopup: async (retestRow) => {
      if (!retestRow || !retestRow.id) return;

      setReferenceRetest(retestRow);
      setOpen(true);
      setLoading(true);
      setRows([]);
      setSelectedIds([]);

      try {
        const res = await ttAPI.getTripHistoryAndDueRetestsFromRetest(
          retestRow.id,
          50
        );

        if (res) {
          setRows(res.rows || []);
          setReferenceAddress(res.reference_site_address || '');
        }
      } catch (err) {
        console.error("NearbyJobsDialog load error", err);
      }

      setLoading(false);
    }
  }));

  const handleClose = () => {
    setOpen(false);
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const openInGoogleMaps = () => {
    const selectedRows = rows.filter((r) =>
      selectedIds.includes(r.ref_id)
    );

    const addresses = selectedRows
      .map((r) => r.site_address)
      .filter(Boolean);

    if (!referenceAddress || addresses.length === 0) return;

    const parts = [referenceAddress, ...addresses].map((a) =>
      encodeURIComponent(a)
    );

    const url = `https://www.google.com/maps/dir/${parts.join("/")}`;
    window.open(url, "_blank");
  };

  const openRow = (row) => {
    if (!row) return;

    if (row.row_type === 'DUE_RETEST' && row.retest_id) {
      window.open(
        `${config.adminApi}/#/app/retest/book/${row.retest_id}`,
        '_blank'
      );
      return;
    }

    if (row.row_type === 'HISTORY' && row.history_job_id) {
      window.open(
        `${config.adminApi}/#/app/job/edit/${row.history_job_id}`,
        '_blank'
      );
    }
  };

  const formatDistance = (row) => {
    if (row.km_from_ref && row.km_from_ref !== '') {
      return row.km_from_ref;
    }
    return row.proximity_hint || '';
  };

  const formatStatusOrDate = (row) => {
    if (row.row_type === 'DUE_RETEST') {
      const status = row.status || '';

      const notes =
        row.notes ||
        (
          referenceRetest &&
          row.retest_id === referenceRetest.id
            ? referenceRetest.notes
            : ''
        );

      if (notes) {
        return `${status} • ${notes}`;
      }

      return status;
    }

    return row.start_time || '';
  };

  const openReferenceRetest = () => {
    if (!referenceRetest || !referenceRetest.id) return;

    window.open(
      `${config.adminApi}/#/app/retest/book/${referenceRetest.id}`,
      '_blank'
    );
  };

  const isReferenceRelated = (row) => {
    if (!referenceRetest) return false;
    return row.site_id === referenceRetest.site_id;
  };

  const sortedRows = [...rows].sort((a, b) => {
    let aValue;
    let bValue;

    if (sortBy === 'assets') {
      aValue = a.assets_to_do ?? -1;
      bValue = b.assets_to_do ?? -1;
    } else if (sortBy === 'distance') {
      aValue = a.distance_from_ref_m ?? Number.MAX_SAFE_INTEGER;
      bValue = b.distance_from_ref_m ?? Number.MAX_SAFE_INTEGER;
    } else {
      return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
    <DialogTitle style={{ fontWeight: 600 }}>
      <div style={{ fontSize: '1.95rem', fontWeight: 900 }}>Nearby Jobs</div>

      {referenceRetest && (
        <div style={{ marginTop: '8px', fontWeight: 400, fontSize: '0.95rem', lineHeight: 1.5 }}>
          <div>
            <strong>Reference retest:</strong> {referenceRetest.customer_name}
          </div>
          <div>
            {referenceRetest.customer_name} - {referenceRetest.site_name}
          </div>
          <div className={classes.address}>
            {referenceRetest.site_address || referenceAddress}
          </div>
          <div>
            <strong>Status:</strong> {referenceRetest.status || ''} {' | '}
            <strong>Assets:</strong> {referenceRetest.email_total_assets ?? referenceRetest.site_total_assets ?? referenceRetest.assets ?? ''}
          </div>
          <div
            onClick={openReferenceRetest}
            style={{
              marginTop: '6px',
              color: '#1976d2',
              cursor: 'pointer',
              textDecoration: 'underline',
              display: 'inline-block'
            }}
          >
            Open retest
          </div>
        </div>
      )}
    </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <TableContainer style={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
            <TableHead>
              <TableRow className={classes.headerRow}>
                <TableCell className={classes.headerCell} />
                <TableCell className={classes.headerCell}>Type</TableCell>
                <TableCell className={classes.headerCell}>Site</TableCell>
                <TableCell className={classes.headerCell}>Locality</TableCell>
                <TableCell
                  className={classes.headerCell}
                  onClick={() => {
                    if (sortBy === 'assets') {
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortBy('assets');
                      setSortDirection('desc');
                    }
                  }}
                  style={{ cursor: 'pointer', width: '120px' }}
                >
                  Assets {sortBy === 'assets' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                </TableCell>
                <TableCell
                     className={classes.headerCell}
                     onClick={() => {
                       if (sortBy === 'distance') {
                         setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                       } else {
                         setSortBy('distance');
                         setSortDirection('asc');
                       }
                     }}
                     style={{ cursor: 'pointer', width: '120px' }}
                    >
                  <Tooltip
                    title="Distance is straight-line (as the crow flies), not road distance. Use with caution."
                    arrow
                    placement="top"
                  >
                    <span style={{ cursor: 'help', borderBottom: '1px dotted #999' }}>
                      Distance {sortBy === 'distance' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell className={classes.headerCell}>Status / Date</TableCell>
              </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.map((row) => (
                  <TableRow
                      key={`${row.row_type}_${row.ref_id}`}
                      hover
                      style={{
                        cursor: 'pointer',
                        backgroundColor: isReferenceRelated(row) ? '#f3f6fb' : undefined
                      }}
                      onClick={() => openRow(row)}
                    >
                    <TableCell
                      padding="checkbox"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Checkbox color="primary"
                        checked={selectedIds.includes(row.ref_id)}
                        onChange={() => toggleSelection(row.ref_id)}
                      />
                    </TableCell>

                    <TableCell>{row.row_type}</TableCell>

                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {row.key_customer && (
                          <Tooltip title="Key customer">
                            <VpnKeyIcon style={{ fontSize: 18, color: '#d4a017' }} />
                          </Tooltip>
                        )}

                        {row.kpi_customer && (
                          <Tooltip title="KPI customer">
                            <TrackChangesIcon style={{ fontSize: 18, color: '#d4a017' }} />
                          </Tooltip>
                        )}

                        <span>{row.customer_name}</span>
                      </div>

                      <div>{row.site_name}</div>

                      <div className={classes.address}>
                        {row.site_address}
                      </div>
                    </TableCell>

                    <TableCell>
                      {row.geo_locality || row.locality || ''}
                    </TableCell>

                    <TableCell>{row.assets_to_do}</TableCell>

                    <TableCell className={classes.distance}>
                      {formatDistance(row)}
                    </TableCell>

                    <TableCell>
                      {formatStatusOrDate(row)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>

        <Button
          onClick={openInGoogleMaps}
          disabled={selectedIds.length === 0}
        >
          Open in Google Maps
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default NearbyJobsDialog;
